import {Component, OnInit, Input} from "@angular/core";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ExamSession, ExamSessionCreateOrUpdateData} from "../../../shared/models/examSession";
import {FormGroup, FormControl} from "@angular/forms";
import {ExamCenter} from "../../../shared/models/examCenter";
import {User} from "../../../shared/models/user";
import {ExamCenterService} from "../../../shared/services/exam-center/exam-center.service";
import {take} from "rxjs/operators";
import {UserService} from "../../../shared/services/user/user.service";
import {GroupsEnum} from "../../../shared/enums/groupsEnum";
import {Group} from "../../../shared/models/group";
import {forkJoin} from "rxjs";
import {ExamSessionService} from "../../../shared/services/exam-session/exam-session.service";
import {environment} from "../../../../environments/environment";
import {ToastrService} from "ngx-toastr";

@Component({
    selector: "app-session-modal",
    templateUrl: "./session-modal.component.html",
    styleUrls: ["./session-modal.component.scss"]
})
export class SessionModalComponent implements OnInit {

    @Input() examSession: ExamSession;
    @Input() allSessions: Array<ExamSession> = [];
    sessionForm: FormGroup;
    examCenters: Array<ExamCenter>;
    supervisors: Array<User>;
    correcteurs: Array<User>;
    isDataLoading: boolean = true;
    user: User;
    baseUrl = environment.baseUrl;
    submitting: boolean = false;

    constructor (
        public activeModal: NgbActiveModal,
        public examCenterService: ExamCenterService,
        public examSessionService: ExamSessionService,
        private userService: UserService,
        private toastr: ToastrService
    ) {
        this.sessionForm = new FormGroup({
            code: new FormControl(""),
            date: new FormControl(""),
            time:  new FormControl(""),
            convocationTime: new FormControl(""),
            center: new FormControl(""),
            supervisor: new FormControl(""),
            correcteur: new FormControl(""),
            correcteur2: new FormControl(""),
            zoomLink: new FormControl(""),
            examLocationDetails: new FormControl(""),
            info: new FormControl(""),
            notifyUsers: new FormControl(true),
            remote: new FormControl(false),
            nbMaxCandidates: new FormControl(1)
        });
        this.isDataLoading = true;
    }

    /*
     * Function to instanciate the frontend form
     * Call only after all data is fetched
     */
    private resetForm () {

        // Populate the form if there's an input
        if (this.examSession !== null) {
            this.sessionForm.setValue({
                code: this.examSession.code,
                date: new Date(this.examSession.date),
                time: this.examSession.date.substring(11, 16),
                convocationTime: this.examSession.convocationTime ? this.examSession.convocationTime : null,
                center: this.examSession.center
                    ? this.examSession.center.id
                    : null,
                supervisor: this.examSession.supervisor
                    ? this.examSession.supervisor.id
                    : null,
                correcteur: this.examSession.correcteur
                    ? this.examSession.correcteur.id
                    : null,
                correcteur2: this.examSession.correcteur2
                    ? this.examSession.correcteur2.id
                    : null,
                zoomLink: this.examSession.zoomLink ? this.examSession.zoomLink : "",
                examLocationDetails: this.examSession.examLocationDetails ? this.examSession.examLocationDetails : "",
                info: this.examSession.info ? this.examSession.info : "",
                notifyUsers: true,
                remote: this.examSession.zoomLink ? true : false,
                nbMaxCandidates: this.examSession.nbMaxCandidates ? this.examSession.nbMaxCandidates : 1
            });
        }

        // If the user is an exam center, set the exam center of the form to be the user's exam center
        if (this.user.isExamCenterBoss) {
            this.sessionForm.controls.center.setValue(this.user.examCenters[0].id);
            this.sessionForm.get("center").disable();
        }
    }

    ngOnInit () {
        const getExamCenters = this.examCenterService.list(true),
            getSupervisorsAndCorrecteurs = this.userService.list([
                GroupsEnum.CORRECTOR,
                GroupsEnum.SUPERVISOR
            ]),
            getUser = this.userService.getMe();

        // Fill user form once all data has been fetched
        forkJoin(
            getExamCenters,
            getSupervisorsAndCorrecteurs,
            getUser
        ).pipe(take(1)).
            subscribe(([
                examCenters,
                users,
                user
            ]) => {
                this.examCenters = examCenters;
                this.supervisors = users.filter((u: User) => u.groups.some((g: Group) => g.name == GroupsEnum.SUPERVISOR));
                this.correcteurs = users.filter((u: User) => u.groups.some((g: Group) => g.name == GroupsEnum.CORRECTOR));
                this.user = user;
                this.resetForm();
                this.isDataLoading = false;
            });
    }

    private validateForm (): boolean {
        Object.keys(this.sessionForm.controls).forEach((key) => {
            this.sessionForm.controls[key].setErrors(null);
        });

        const convocationTime = this.sessionForm.controls.convocationTime.value;
        const time = this.sessionForm.controls.time.value;
        if (convocationTime && time && convocationTime > time) {
            this.sessionForm.controls.convocationTime.setErrors({validationError: true});
            this.toastr.error(
                "L'heure de convocation ne peut pas être après l'heure de début de session",
                "Erreur de saisie"
            );
            return false;
        }

        const code = this.sessionForm.controls.code.value;
        const codeAlreadyUsed = this.allSessions.some(
            (s) => s.code === code && (!this.examSession || s.id !== this.examSession.id)
        );
        if (codeAlreadyUsed) {
            this.sessionForm.controls.code.setErrors({validationError: true});
            this.toastr.error("Ce nom de session est déjà utilisé", "Erreur de saisie");
            return false;
        }

        const correcteur = this.sessionForm.controls.correcteur.value;
        const correcteur2 = this.sessionForm.controls.correcteur2.value;
        if (correcteur && correcteur2 && correcteur === correcteur2) {
            this.sessionForm.controls.correcteur2.setErrors({validationError: true});
            this.toastr.error(
                "Les deux correcteurs sélectionnés ne peuvent pas être identiques",
                "Erreur de saisie"
            );
            return false;
        }

        if (!this.examSession) {
            const today = new Date();
            today.setHours(
                0,
                0,
                0,
                0
            );
            const selectedDate = new Date(this.sessionForm.controls.date.value);
            selectedDate.setHours(
                0,
                0,
                0,
                0
            );
            if (selectedDate < today) {
                this.sessionForm.controls.date.setErrors({validationError: true});
                this.toastr.error(
                    "La date ne peut pas être antérieure à aujourd'hui",
                    "Erreur de saisie"
                );
                return false;
            }
        }

        return true;
    }

    validate () {
        if (!this.validateForm()) {
            return;
        }
        this.submitting = true;

        const date = new Date(this.sessionForm.controls.date.value);

        const isoDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().
            substring(
                0,
                10
            );

        const isoDateTime = `${isoDate} ${this.sessionForm.controls.time.value}:00`;

        const isoConvocationTime = `${this.sessionForm.controls.convocationTime.value}:00`;

        const body: ExamSessionCreateOrUpdateData = {
            "code": this.sessionForm.controls.code.value,
            "date": isoDateTime,
            "convocation_time": isoConvocationTime,
            "center_id": this.sessionForm.controls.center.value,
            "notify_users": this.sessionForm.controls.notifyUsers.value,
            "info": this.sessionForm.controls.info.value,
            "nb_max_candidates": this.sessionForm.controls.nbMaxCandidates.value
            
        };

        if (this.sessionForm.controls.remote.value) {
            if (!this.sessionForm.controls.zoomLink.value) {
                this.toastr.error(
                    "Vous devez renseigner un lien Zoom pour une session à distance",
                    "Erreur"
                );
                this.submitting = false;
                return;
            } else {
                body.zoom_link = this.sessionForm.controls.zoomLink.value;
                body.exam_location_details = null;
            }

        } else {
            if (!this.sessionForm.controls.center.value) {
                this.toastr.error(
                    "Vous devez renseigner un centre d'examen pour une session en présentiel",
                    "Erreur"
                );
                this.submitting = false;
                return;
            } else {
                body.exam_location_details = this.sessionForm.controls.examLocationDetails.value;
                body.zoom_link = null;
            }
        }

        if (this.sessionForm.controls.supervisor.value) {
            // eslint-disable-next-line camelcase
            body.supervisor_id = this.sessionForm.controls.supervisor.value;
        }

        if (this.sessionForm.controls.correcteur.value) {
            // eslint-disable-next-line camelcase
            body.correcteur_id = this.sessionForm.controls.correcteur.value;
        }

        if (this.sessionForm.controls.correcteur2.value) {
            // eslint-disable-next-line camelcase
            body.correcteur_2_id = this.sessionForm.controls.correcteur2.value;
        }

        // Trigger update if it's an edit
        if (this.examSession) {
            this.examSessionService.update(
                this.examSession.id,
                body
            ).pipe(take(1)).
                subscribe(
                    (res: ExamSession) => {
                        this.examSession = res;
                        this.resetForm();
                        this.activeModal.close(this.examSession);
                        this.submitting = false;
                    },
                    (err) => {
                        if (err.status === 597) {
                            this.toastr.error(
                                "Attention ! l'email de confirmation n'a pas pu être envoyé",
                                "Opération enregistrée"
                            );
                        } else {
                            this.toastr.error(
                                "Une erreur est survenue",
                                "Erreur"
                            );
                        }

                        this.submitting = false;
                        throw err;
                    }
                );

        // Trigger a save otherwise
        } else {
            this.examSessionService.create(body).pipe(take(1)).
                subscribe(
                    (res: ExamSession) => {
                        this.examSession = res;
                        this.resetForm();
                        this.activeModal.close(this.examSession);
                        this.submitting = false;
                    },
                    (err) => {
                        if (err.status === 597) {
                            this.toastr.error(
                                "Attention ! l'email de confirmation n'a pas pu être envoyé",
                                "Opération enregistrée"
                            );
                        } else {
                            this.toastr.error(
                                "Une erreur est survenue",
                                "Erreur"
                            );
                        }

                        this.submitting = false;
                        throw err;
                    }
                );
        }
    }
}
